import { I18n } from '@byted-cg/linkchat-plugin';
import { inject, observer } from 'mobx-react';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import { AppContextType } from '@byted-cg/linkchat-plugin';
import { Popover } from '@byted-cg/linkchat-ui';

import { Icon } from '../components/Icon';
import classes from '../constants/classes';
import { LayoutEventBus } from '../constants/eventBus';
import { prefixCls } from '../utils/style';
import { usePanel } from './hooks/usePanel';
import LazyComponent from './LazyComponent';
import { IToolButton, IToolPanelConfig, ToolTypes } from './type';
import closeIcon from '../../../../public/close.png';
import openIcon from '../../../../public/open.png';
import { MESSAGE_ALL_TYPE } from '../../../linkchat/src/constants';
import { SEND_TYPE } from '../../../linkchat/src/constants/status';
import getTextMessageElement from '../../../../src/utils/getTextMessageElement';
import getTextFromHtmlStr from '../../../../src/utils/getTextFromHtmlStr';

enum EStatus {
  hidden = 'hidden',
  show = 'show',
}
export interface IOptionalRef {
  toggleStatus: (status?: string) => void;
}
interface OptionalOptions {
  appContext?: AppContextType;
  toolPanelList?: IToolPanelConfig;
  config?: string[];
  children?: React.ReactElement;
}
const Optional = React.forwardRef<IOptionalRef, OptionalOptions>(
  ({ children, config, toolPanelList, appContext }, ref) => {
    const [currentPanel] = usePanel();
    const [currentStatus, setCurrentStatus] = useState<string>(EStatus.show);

    const openKeyboardPanel = () => {
      appContext?.$refs?.$usePanel?.current?.toggle('keyboard');
    };
    const openEmojiPanel = () => {
      if (currentPanel !== 'emoji') {
        appContext?.eventBus?.publish(LayoutEventBus.emojiOnOpen);
        return appContext?.$refs?.$usePanel?.current?.toggle('emoji');
      }
      appContext?.eventBus?.publish(LayoutEventBus.emojiOnClose);
      return appContext?.$refs?.$usePanel?.current?.toggle(false);
    };
    const openToolPanel = () => {
      appContext?.eventBus?.publish(LayoutEventBus.toolsOnOpen);
      appContext?.$refs?.$usePanel?.current?.toggle('tool');
    };
    const closeToolPanel = () => {
      appContext?.eventBus?.publish(LayoutEventBus.toolsOnClose);
      appContext?.$refs?.$usePanel?.current?.toggle(false);
    };

    const useFunction =
      (config: IToolButton = {} as IToolButton) =>
      () => {
        switch (config.type) {
          case ToolTypes.cmd:
            appContext?.cmd?.send(config.data);
            break;
          case ToolTypes.url:
            appContext?.jsBrige?.openWindow(config.data);
            break;
          default:
            if (config.data && typeof config.data === 'function') {
              config.data(appContext, config);
            }
            break;
        }
      };
    const contentElement = useMemo(() => {
      if (!appContext?.replyMessage?.serverId) {
        return '';
      }
      let { senderType, content, recalled } = appContext?.replyMessage || {};
      if (recalled) {
        return I18n.t('message_recalled', {}, '此消息已撤回');
      }

      switch (Number(appContext?.replyMessage?.type)) {
        case MESSAGE_ALL_TYPE.MESSAGE_TYPE_IMAGE:
          return I18n.t('[picture]', {}, '[图片]');
        case MESSAGE_ALL_TYPE.MESSAGE_TYPE_FILE:
          return I18n.t('[file]', {}, '[文件]');
        case MESSAGE_ALL_TYPE.ROBOT_THUMBS_TYPE:
          return I18n.t('[rich_text]', {}, '[富文本]');
        case MESSAGE_ALL_TYPE.CUSTOM_VIDEO_TYPE:
        case MESSAGE_ALL_TYPE.MESSAGE_TYPE_VIDEO:
          return I18n.t('[video]', {}, '[视频]');
        case MESSAGE_ALL_TYPE.MESSAGE_TYPE_TEXT:
          let _result = getTextMessageElement(
            senderType === SEND_TYPE.USER
              ? content
              : getTextFromHtmlStr(content),
            {
              email: { disable: true },
              url: { disable: true },
              emoji: { size: 14 },
            },
          );
          return _result;
        default:
          return I18n.t('[card]', {}, '[卡片]');
      }
    }, [appContext?.replyMessage]);

    const handleCancelReply = () => {
      appContext.replyMessage = {};
    };

    useImperativeHandle(ref, () => {
      return {
        toggleStatus: (status) => {
          status && setCurrentStatus(status);
        },
      };
    });
    const EmjioIcon = useMemo(() => {
      return !appContext?.query?.isPc ? (
        <Fragment>
          <Icon
            className={prefixCls(
              {
                toolItem: true,
                EmjioToolIcon: true,
                hidden: currentPanel !== 'emoji',
              },
              appContext?.query?.isPc || false,
            )}
            key="Keyboard"
            type="Keyboard"
            onClick={openKeyboardPanel}
          />
          <Icon
            className={prefixCls(
              {
                toolItem: true,
                EmjioToolIcon: true,
                hidden: currentPanel === 'emoji',
              },
              appContext?.query?.isPc || false,
            )}
            key="Emotion"
            type="Emotion"
            onClick={openEmojiPanel}
          />
        </Fragment>
      ) : (
        <Popover placement="top" content={I18n.t('expression', {}, '表情')}>
          <Icon
            className={prefixCls(
              {
                EmjioToolIcon: true,
                toolItem: true,
                'toolItem--hover': currentPanel !== 'emoji',
              },
              appContext?.query?.isPc || false,
            )}
            key="Emotion"
            type="Emotion"
            onClick={openEmojiPanel}
          />
        </Popover>
      );
    }, [appContext?.query?.isPc, openEmojiPanel, openToolPanel, currentPanel]);
    return currentStatus !== EStatus.hidden ? (
      <div
        className={prefixCls(
          {
            [`${classes.prefix}OptionalDiv`]: true,
            hasSafeBottom: !currentPanel,
          },
          appContext?.query?.isPc,
        )}
      >
        {contentElement && (
          <div className="replyMessageContainer">
            <div className="replyClose">
              <Icon
                className="replyCloseIcon"
                onClick={handleCancelReply}
                type="Close"
                fill="#999"
                size={18}
              ></Icon>
            </div>
            <div className="replyLine"></div>
            <div className="replyContent">
              <div className="replyTitle">{I18n.t('reply', {}, '回复')}:</div>
              <div className="replyElement">{contentElement}</div>
            </div>
          </div>
        )}
        <div className="buttonInputTools">
          <div className="buttonGroup buttonGroup__left">
            {config && config.length && toolPanelList?.length
              ? toolPanelList.map((itemConfig, index) =>
                  config.indexOf(itemConfig.label) > -1 ? (
                    <div
                      className="toolItem"
                      key={`${itemConfig.label}--${index}`}
                      onClick={useFunction(itemConfig)}
                    >
                      {typeof itemConfig?.icon === 'string' ? (
                        <Icon
                          key={`${itemConfig.label}--${index}`}
                          className="toolItemIcon"
                          type={itemConfig.icon as 'Folder'}
                        />
                      ) : (
                        <LazyComponent
                          className="toolItem"
                          key={`${itemConfig.label}--${index}`}
                          config={itemConfig.icon}
                          data={itemConfig.data}
                        />
                      )}
                    </div>
                  ) : null,
                )
              : null}
          </div>
          {children &&
            React.cloneElement(children, {
              children: EmjioIcon,
              rightButtonList: (
                <div className="buttonGroup buttonGroup__right">
                  {
                    <>
                      <img
                        onClick={closeToolPanel}
                        className={prefixCls(
                          {
                            toolItem: true,
                            hidden: currentPanel !== 'tool',
                          },
                          appContext?.query?.isPc || false,
                        )}
                        src={closeIcon}
                      />
                      <img
                        onClick={openToolPanel}
                        className={prefixCls(
                          {
                            toolItem: true,
                            hidden: currentPanel === 'tool',
                          },
                          appContext?.query?.isPc || false,
                        )}
                        src={openIcon}
                      />
                    </>
                  }
                </div>
              ),
            })}
        </div>
      </div>
    ) : null;
  },
);

export default inject(({ appContext }) => ({
  appContext,
}))(observer(Optional));
